import './List.scss';

import { Alert }                    from '@/components/ui/alert';
import ApiModel                     from '@widesk-core/models/ApiModel';
import BlockIcon                    from '@/assets/icons/BlockIcon';
import { Box }                      from '@chakra-ui/react';
import { EmptyState }               from '@/components/ui/empty-state';
import { HStack }                   from '@chakra-ui/react';
import { LIST_ITEMS_PER_PAGE }      from '@/constants';
import ListItem                     from '@/components/list/components/ListItem';
import PagedCollection              from '@widesk-core/models/PagedCollection';
import { PaginationItems }          from '@/components/ui/pagination';
import { PaginationNextTrigger }    from '@/components/ui/pagination';
import { PaginationPrevTrigger }    from '@/components/ui/pagination';
import { PaginationRoot }           from '@/components/ui/pagination';
import React                        from 'react';
import { SelectContent }            from '@/components/ui/select';
import { SelectItem }               from '@/components/ui/select';
import { SelectRoot }               from '@/components/ui/select';
import { SelectTrigger }            from '@/components/ui/select';
import { SelectValueText }          from '@/components/ui/select';
import Spinner                      from '@/components/spinner/Spinner';
import { Text }                     from '@chakra-ui/react';
import _uniqueId                    from 'lodash/uniqueId'
import { createListCollection }     from '@chakra-ui/react';
import getErrorMessage               from '@/tools/getErrorMessage';
import { observer }                 from 'mobx-react';
import { toaster }                  from '@/components/ui/toaster';
import useFetch                     from '@widesk-core/hooks/useFetch';
import useScreenSize                from '@/hooks/useScreenSize';
import { useTranslation }           from 'react-i18next';
import { Link }                     from 'react-router-dom';

interface IListProps<T extends ApiModel> {
	afterFetch?: () => void | Promise<void>;
	beforeFetch?: () => void | Promise<void>;
	collection: PagedCollection<T>;
	renderRow: (model: T) => JSX.Element;
	onItemClick?: (model: T) => void;
	options?: {
		clickable?: boolean;
		hideErrorMessage?: boolean;
		emptyAlert?: {
			description?: string;
			icon?: JSX.Element;
			title: JSX.Element | string;
		}
		emptyMessages?: {
			description?: string;
			icon?: JSX.Element;
			title: string;
		}
		filters?: ModelFilters<T>
		itemsPadding?: string;
		itemsMargin?: string;
		margin?: string;
		padding?: string;
		maxWidth?: string;
		withPagination?: boolean;
		withSeparators?: boolean;
		bottomLink?: {
			label: string;
			to: string;
		};
	},
	getFetch?: (fetch: (() => Promise<void>)) => void;
	sort?: { name: keyof ModelSorts<T>, way: SortWay };
}

const List = observer(<T extends ApiModel>(props: IListProps<T>) => {
	const {
		afterFetch,
		beforeFetch,
		collection,
		renderRow,
		onItemClick,
		options,
		sort,
	} = props;

	const { t } = useTranslation();
	const { isLG } = useScreenSize();
	const [itemsPerPage, setItemsPerPage] = React.useState(
		parseInt(localStorage.getItem(LIST_ITEMS_PER_PAGE) || '10'),
	);
	const [page, setPage] = React.useState(1);

	const filtersKey = React.useMemo(() => {
		return JSON.stringify(options?.filters || {});
	}, [options?.filters]);

	React.useEffect(() => {
		setPage(1);
	}, [filtersKey]);

	const itemsPerPageOptions = createListCollection({
		items: ['5', '10', '20', '50'].map(itemsPerPage => ({ label: t(`app.selects.itemsPerPage.${itemsPerPage}`), value: itemsPerPage })),
	});

	const selectedPageOption = itemsPerPageOptions.items.find((option) => parseInt(option.value) === itemsPerPage);

	const { error, fetchAsync, isLoading, isLoaded } = useFetch(async () => {
		
		collection.clear();

		if (beforeFetch) {
			await beforeFetch();
		}

		if (options?.filters) {
			collection.setFilters(options.filters)
		}

		localStorage.setItem(LIST_ITEMS_PER_PAGE, itemsPerPage.toString());

		if (sort) {
			collection.setSort(sort.name, sort.way)
		}

		await collection
			.setItemsPerPage(itemsPerPage)
			.setPage(page)
			.list();

		if (afterFetch) {
			await afterFetch();
		}

		return collection;
	});

	props.getFetch?.(fetchAsync);

	React.useEffect(() => {
		(() => fetchAsync())();
	}, [itemsPerPage, page, JSON.stringify(options?.filters)]);

	React.useEffect(() => {
		if (options?.hideErrorMessage) {
			return;
		}
		if (error) {
			toaster.create({
				title: t('app.errors.unknown'),
				type: 'error',
			})
			console.log(error);
		}
	}, [(error as any)?.message]);

	if (isLoading || collection.isLoading) {
		return (
			<Box py={20}>
				<Spinner />
			</Box>
		);
	}

	if (!options?.hideErrorMessage && error) {
		return <Alert status="error" title={getErrorMessage(error, t)} />;
	}

	const total = collection.total || collection.length;
	
	// La liste est vide si la collection est chargée ET vide
	// La liste est aussi vide si il y a eu une erreur ET que l'on souhaites volontairement ne pas afficher cette erreur à l'utilisteur
	const isEmpty = (error && options?.hideErrorMessage) || (isLoaded && collection?.length === 0)

	return (
		<>
			{
				isEmpty 
				&& options?.emptyAlert 
				&& (
					<Alert title={options?.emptyAlert?.title} />
				)
			}

			<Box
				display="flex"
				flexDirection="column"
				gap="20px"
			>
				<Box
					display="flex"
					flexDirection="column"
					bg="white"
					rounded="md"
					shadow="md"
					padding={options?.padding}
					maxWidth={options?.maxWidth}
				>
					{isEmpty ?
						<EmptyState
							icon={options?.emptyMessages?.icon || <BlockIcon size="xl" />}
							title={options?.emptyMessages?.title || ''}
							description={options?.emptyMessages?.description}
							justifyContent="center"
							alignContent="center"
						/>
						:
						collection.map((item, index) => {
							return (
								<ListItem
									index={index || 0}
									item={item}
									key={item.urn || _uniqueId()}
									listLength={total}
									options={options}
									renderRow={renderRow}
									onClick={onItemClick}
								/>
							);
						})
					}
					{
						!!options?.bottomLink &&
						<Box 
							display="flex"
							alignItems="center"
							justifyContent="center"
							borderTop="1px solid #e4e4e7"
							paddingTop="20px"
							paddingBottom="20px"
						>
							<Link to={options.bottomLink.to}>
								<Text className="poppins-semibold" textDecoration="underline" fontSize={14}>{options.bottomLink.label}</Text>
							</Link>
						</Box>
					}
				</Box>
				{
					options?.withPagination === false ?
					<></>
					:
					total > 0 &&
					<Box
						display="flex"
						flexDirection={isLG ? 'row' : 'column'}
						alignItems={isLG ? 'center' : 'flex-end'}
						justifyContent="space-between"
						gap="15px"
					>
						<Box
							display="flex"
							flexDirection={isLG ? 'row' : 'row-reverse'}
							alignItems="center"
							gap="15px"
						>
							<SelectRoot
								className="select-items-per-page"
								bg="white"
								borderRadius="xl"
								defaultValue={[selectedPageOption?.value || '10']}
								collection={itemsPerPageOptions}
								size="sm"
								width="130px"
								onValueChange={(option) => {
									const value = option.value[0];
									if (value) {
										setPage(1);
										setItemsPerPage(parseInt(value))
									}
								}}
							>
								<SelectTrigger>
									<SelectValueText />
								</SelectTrigger>

								<SelectContent>
									{itemsPerPageOptions.items.map((option) => (
										<SelectItem
											_hover={{ cursor: 'pointer', backgroundColor: '#DEDEDE' }}
											item={option}
											key={option.value}
										>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</SelectRoot>

							<Text fontSize="12px">
								{t('app.pagination.count', { count: collection.length || 0, total })}
							</Text>
						</Box>

						<PaginationRoot
							count={total}
							page={page}
							pageSize={itemsPerPage}
							variant="solid"
							onPageChange={(change) => setPage(change.page)}
							size={isLG ? 'sm' : 'xs'}
						>
							<HStack>
								<PaginationPrevTrigger />
								<PaginationItems />
								<PaginationNextTrigger />
							</HStack>
						</PaginationRoot>
					</Box>
				}
			</Box>
		</>
	)
});

export default List